import { useEffect, useRef, useState, useCallback } from 'react'
import { io, Socket } from 'socket.io-client'

// Tipos para eventos WebSocket
export interface WhatsAppMessage {
  id: string
  content: string
  fromMe: boolean
  timestamp: string
  pushName?: string
  messageType?: string
  mediaUrl?: string | null
  metadata?: any
  isBotResponse?: boolean
}

export interface ConversationMetadata {
  remote_jid: string
  remote_name: string | null
  remote_profile_pic: string | null
  is_bot_active: number
  bot_status: string
  is_archived: number
  // Contato na blocklist (golpista). Vem no emit new_message pra conversa que
  // nasce em tempo real já aparecer com o badge vermelho na inbox.
  blocked?: { reason: string | null; blocked_at: string | null; blocked_by: string | null } | null
}

export interface ConversationUpdate {
  conversationId: string
  conversation?: ConversationMetadata
  message: WhatsAppMessage
}

export interface BotTypingEvent {
  conversationId: string
  typing: boolean
}

export interface BotStatusEvent {
  deviceId: string
  enabled: boolean
}

export interface ConnectionStatusEvent {
  deviceId: string
  state: string
  statusReason?: string
}

export interface ConversationBotToggledEvent {
  conversationId: string
  isActive: boolean
  botStatus?: string
  reason?: string
  operator?: string
}

export interface ConversationUpdatedEvent {
  conversationId: string
  profilePic?: string
}

export interface HumanTakeoverEvent {
  conversationId: string
  deviceId: string
  pausedUntil: string
  operator?: string
  markerId?: string
}

export interface BotAutoResumedEvent {
  conversationId: string
  deviceId: string
  markerId?: string
}

export interface BotManualResumedEvent {
  conversationId: string
  deviceId: string
  operator?: string
  markerId?: string
}

export interface DeviceAIToggledEvent {
  deviceId: string
  enabled: boolean
  conversationsReset: number
  operator?: string
  markerId?: string
}

// Status delivery vindo dos webhooks da Meta (sent → delivered → read).
// Emitido pelo backend em handleMetaStatus para mensagens fora-de-campanha.
export interface MessageStatusUpdateEvent {
  conversationId: string
  messageId: string
  status: 'sent' | 'delivered' | 'read' | 'failed'
}

// Falha de entrega numa mensagem de handoff (template pra líder ou "Pronto" pra
// lead). Emitido em handleMetaStatus quando status=failed e metadata.source ∈
// {lead_handoff_template, lead_handoff_notify, manual_template, auto_assign_leader}.
// Caso real: Josimara 2026-05-26 9:44 — Meta marcou failed mas atendente nunca soube.
export interface LeadHandoffDeliveryFailedEvent {
  conversationId: string
  messageId: string
  source: 'lead_handoff_template' | 'lead_handoff_notify' | 'manual_template' | 'auto_assign_leader' | 'forward' | 'leader_followup_template'
  errorCode: number | null
  errorTitle: string | null
  cadastroError?: boolean         // true = as DUAS variantes do 9º dígito falharam (número cadastrado errado)
  recipient: string
  leaderName: string | null
  leaderId: string | null         // cadastro_clientes.id da líder — usado pelo retry pra refazer handoff
  leaderCode: string | null
  leaderPhoneSource: 'celular' | 'telefone1' | 'telefone2' | null  // qual campo do cadastro falhou — exclui do retry
  leadConversationId: string | null
  deviceId: string | null         // necessário pro retry chamar /handoff-leader
  contentPreview: string
}

// Lead que o auto-assign segurou pro humano (sem CEP / geo ruim / sem líder no raio).
export interface LeadAutoAssignHeldEvent {
  conversationId: string
  deviceId: string | null
  leadName: string | null
  leadCep: string | null
  reason: 'no_cep' | 'low_confidence_geo' | 'no_proven_in_radius' | string
}

// Lead segurado ganhou líder (atribuição manual persistida) — a UI tira o
// aviso do banner sem esperar F5.
export interface LeadAutoAssignReleasedEvent {
  conversationId: string
  deviceId: string | null
}

// Tipos de callbacks
interface WhatsAppSocketCallbacks {
  onNewMessage?: (data: ConversationUpdate) => void
  onBotResponse?: (data: ConversationUpdate) => void
  onBotTyping?: (data: BotTypingEvent) => void
  onBotStatusChanged?: (data: BotStatusEvent) => void
  onConnectionStatus?: (data: ConnectionStatusEvent) => void
  onConversationBotToggled?: (data: ConversationBotToggledEvent) => void
  onConversationUpdated?: (data: ConversationUpdatedEvent) => void
  onMessageSent?: (data: ConversationUpdate) => void
  onHumanTakeover?: (data: HumanTakeoverEvent) => void
  onBotAutoResumed?: (data: BotAutoResumedEvent) => void
  onBotManualResumed?: (data: BotManualResumedEvent) => void
  onDeviceAIToggled?: (data: DeviceAIToggledEvent) => void
  onMessageStatusUpdate?: (data: MessageStatusUpdateEvent) => void
  onLeadHandoffDeliveryFailed?: (data: LeadHandoffDeliveryFailedEvent) => void
  onLeadAutoAssignHeld?: (data: LeadAutoAssignHeldEvent) => void
  onLeadAutoAssignReleased?: (data: LeadAutoAssignReleasedEvent) => void
  onConnect?: () => void
  onDisconnect?: () => void
  onError?: (error: Error) => void
}

interface UseWhatsAppSocketOptions {
  deviceId?: string
  autoConnect?: boolean
  callbacks?: WhatsAppSocketCallbacks
}

interface UseWhatsAppSocketReturn {
  isConnected: boolean
  socket: Socket | null
  joinDevice: (deviceId: string) => void
  leaveDevice: (deviceId: string) => void
  markRead: (conversationId: string) => void
  toggleConversationBot: (conversationId: string, isActive: boolean, userId?: string) => void
  resumeConversationBot: (conversationId: string, userId?: string) => void
  connect: () => void
  disconnect: () => void
}

const SOCKET_URL = window.location.origin
const SOCKET_PATH = '/socket.io'
const SOCKET_NAMESPACE = '/whatsapp'

export function useWhatsAppSocket(options: UseWhatsAppSocketOptions = {}): UseWhatsAppSocketReturn {
  const { deviceId, autoConnect = true, callbacks = {} } = options

  const [isConnected, setIsConnected] = useState(false)
  const socketRef = useRef<Socket | null>(null)
  const callbacksRef = useRef(callbacks)

  // Atualizar ref de callbacks quando mudar
  useEffect(() => {
    callbacksRef.current = callbacks
  }, [callbacks])

  // Função para conectar
  const connect = useCallback(() => {
    if (socketRef.current?.connected) {
      return
    }

    // Obter token do localStorage
    const token = localStorage.getItem('auth-token')

    const socket = io(`${SOCKET_URL}${SOCKET_NAMESPACE}`, {
      path: SOCKET_PATH,
      transports: ['websocket', 'polling'],
      auth: {
        token
      },
      reconnection: true,
      // Painel de operação fica aberto o dia todo: NUNCA desistir de reconectar.
      // Com o cap antigo (5 tentativas) um blip de rede, sleep do notebook ou
      // restart do backend matava o socket de vez e a lista só voltava no F5.
      reconnectionAttempts: Infinity,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000
    })

    // Eventos de conexão
    socket.on('connect', () => {
      console.log('[WebSocket] Conectado:', socket.id)
      setIsConnected(true)
      callbacksRef.current.onConnect?.()

      // Se tiver deviceId, entrar na sala automaticamente
      if (deviceId) {
        socket.emit('join_device', deviceId)
      }
    })

    socket.on('disconnect', (reason) => {
      console.log('[WebSocket] Desconectado:', reason)
      setIsConnected(false)
      callbacksRef.current.onDisconnect?.()
    })

    socket.on('connect_error', (error) => {
      console.error('[WebSocket] Erro de conexão:', error)
      callbacksRef.current.onError?.(error)
    })

    // Confirmação de entrada na sala
    socket.on('joined_device', ({ deviceId, room }) => {
      console.log(`[WebSocket] Entrou na sala ${room} do device ${deviceId}`)
    })

    // Eventos de mensagens
    socket.on('new_message', (data: ConversationUpdate) => {
      console.log('[WebSocket] Nova mensagem:', data)
      callbacksRef.current.onNewMessage?.(data)
    })

    socket.on('bot_response', (data: ConversationUpdate) => {
      console.log('[WebSocket] Resposta do bot:', data)
      callbacksRef.current.onBotResponse?.(data)
    })

    socket.on('message_sent', (data: ConversationUpdate) => {
      console.log('[WebSocket] Mensagem enviada:', data)
      callbacksRef.current.onMessageSent?.(data)
    })

    // Eventos de status
    socket.on('bot_typing', (data: BotTypingEvent) => {
      callbacksRef.current.onBotTyping?.(data)
    })

    socket.on('bot_status_changed', (data: BotStatusEvent) => {
      console.log('[WebSocket] Status do bot alterado:', data)
      callbacksRef.current.onBotStatusChanged?.(data)
    })

    socket.on('connection_status', (data: ConnectionStatusEvent) => {
      console.log('[WebSocket] Status de conexão:', data)
      callbacksRef.current.onConnectionStatus?.(data)
    })

    socket.on('conversation_bot_toggled', (data: ConversationBotToggledEvent) => {
      console.log('[WebSocket] Bot da conversa alterado:', data)
      callbacksRef.current.onConversationBotToggled?.(data)
    })

    socket.on('conversation_updated', (data: ConversationUpdatedEvent) => {
      console.log('[WebSocket] Conversa atualizada:', data)
      callbacksRef.current.onConversationUpdated?.(data)
    })

    // Novos eventos do sistema hierárquico de controle do bot
    socket.on('human_takeover', (data: HumanTakeoverEvent) => {
      console.log('[WebSocket] Humano assumiu conversa:', data)
      callbacksRef.current.onHumanTakeover?.(data)
    })

    socket.on('bot_auto_resumed', (data: BotAutoResumedEvent) => {
      console.log('[WebSocket] Bot auto-reativado:', data)
      callbacksRef.current.onBotAutoResumed?.(data)
    })

    socket.on('bot_manual_resumed', (data: BotManualResumedEvent) => {
      console.log('[WebSocket] Bot reativado manualmente:', data)
      callbacksRef.current.onBotManualResumed?.(data)
    })

    socket.on('device_ai_toggled', (data: DeviceAIToggledEvent) => {
      console.log('[WebSocket] IA do device alterada:', data)
      callbacksRef.current.onDeviceAIToggled?.(data)
    })

    // Status delivery (sent/delivered/read) — atualiza checks em tempo real
    // sem precisar reabrir a conversa.
    socket.on('message_status_update', (data: MessageStatusUpdateEvent) => {
      callbacksRef.current.onMessageStatusUpdate?.(data)
    })

    // Mensagem de handoff (template pra líder OU "Pronto" pra lead) marcada
    // como failed pela Meta. UI usa isso pra alertar o operador.
    socket.on('lead_handoff_delivery_failed', (data: LeadHandoffDeliveryFailedEvent) => {
      console.warn('[WebSocket] Falha de entrega em handoff:', data)
      callbacksRef.current.onLeadHandoffDeliveryFailed?.(data)
    })

    // Lead que o auto-assign segurou pro humano — UI alerta pra atribuição manual.
    socket.on('lead_auto_assign_held', (data: LeadAutoAssignHeldEvent) => {
      console.warn('[WebSocket] Lead segurado pelo auto-assign:', data)
      callbacksRef.current.onLeadAutoAssignHeld?.(data)
    })

    // Lead segurado ganhou líder — UI remove o aviso do banner em tempo real.
    socket.on('lead_auto_assign_released', (data: LeadAutoAssignReleasedEvent) => {
      console.log('[WebSocket] Lead segurado liberado (líder atribuída):', data)
      callbacksRef.current.onLeadAutoAssignReleased?.(data)
    })

    socketRef.current = socket
  }, [deviceId])

  // Função para desconectar
  const disconnect = useCallback(() => {
    if (socketRef.current) {
      socketRef.current.disconnect()
      socketRef.current = null
      setIsConnected(false)
    }
  }, [])

  // Funções de controle
  const joinDevice = useCallback((deviceId: string) => {
    if (socketRef.current?.connected) {
      socketRef.current.emit('join_device', deviceId)
    }
  }, [])

  const leaveDevice = useCallback((deviceId: string) => {
    if (socketRef.current?.connected) {
      socketRef.current.emit('leave_device', deviceId)
    }
  }, [])

  const markRead = useCallback((conversationId: string) => {
    if (socketRef.current?.connected) {
      socketRef.current.emit('mark_read', { conversationId })
    }
  }, [])

  const toggleConversationBot = useCallback((conversationId: string, isActive: boolean, userId?: string) => {
    if (socketRef.current?.connected) {
      socketRef.current.emit('toggle_conversation_bot', { conversationId, isActive, userId })
    }
  }, [])

  const resumeConversationBot = useCallback((conversationId: string, userId?: string) => {
    if (socketRef.current?.connected) {
      socketRef.current.emit('resume_conversation_bot', { conversationId, userId })
    }
  }, [])

  // Conectar automaticamente
  useEffect(() => {
    if (autoConnect) {
      connect()
    }

    return () => {
      disconnect()
    }
  }, [autoConnect, connect, disconnect])

  // Entrar na sala do device quando deviceId mudar
  useEffect(() => {
    if (deviceId && socketRef.current?.connected) {
      joinDevice(deviceId)

      return () => {
        leaveDevice(deviceId)
      }
    }
  }, [deviceId, joinDevice, leaveDevice])

  return {
    isConnected,
    socket: socketRef.current,
    joinDevice,
    leaveDevice,
    markRead,
    toggleConversationBot,
    resumeConversationBot,
    connect,
    disconnect
  }
}

export default useWhatsAppSocket
